/**
 * NumberDisplay - Stats card with a header row (icon + label) and a big number
 * row with an optional trend chip. Animated value transitions are preserved.
 */

import { BaseUIComponent } from './BaseUIComponent.js';

import type { BaseUIComponentOptions, UIComponentMetadata } from './BaseUIComponent.js';
import type * as ENGINE from '../../index.js';

export type NumberDisplayTrendDirection = 'up' | 'down' | 'flat';

export interface NumberDisplayTrend {
  /** Magnitude shown next to the arrow (interpreted as percentage). */
  value: number;
  /** Direction of the trend arrow. */
  direction: NumberDisplayTrendDirection;
}

export interface NumberDisplayOptions extends BaseUIComponentOptions {
  /** Text prefix to display before the number */
  prefix?: string;
  /** Text suffix to display after the number */
  suffix?: string;
  /** Initial number value */
  initialValue?: number;
  /** Minimum allowed value */
  minValue?: number;
  /** Maximum allowed value */
  maxValue?: number;
  /** Number of decimal places to display */
  decimalPlaces?: number;
  /** Whether to animate value changes */
  animate?: boolean;
  /** Animation duration in milliseconds */
  animationDuration?: number;
  /** Optional label shown above the number. */
  label?: string;
  /** Optional inline SVG / HTML for the icon slot (20×20). */
  iconHtml?: string;
  /** Optional trend chip shown on the right of the number row. */
  trend?: NumberDisplayTrend | null;
}

export class NumberDisplay extends BaseUIComponent<NumberDisplayOptions> {
  public static metadata: UIComponentMetadata = {
    displayName: 'Number Display',
    category: 'data',
    summary: 'Stats card with icon, label, animated value, and optional trend chip.',
    useCases: ['score', 'gold', 'coins', 'currency', 'counter', 'level', 'kills', 'stat card', 'stats display'],
    optionsType: 'NumberDisplayOptions',
    assetPaths: {
      template: '@engine/assets/ui/templates/number-display.html',
      styles: '@engine/assets/ui/styles/number-display.css',
    },
  };

  private valueElement: HTMLElement | null = null;
  private labelElement: HTMLElement | null = null;
  private iconElement: HTMLElement | null = null;
  private trendElement: HTMLElement | null = null;
  private value: number;
  private animationId: number | null = null;

  constructor(ui: ENGINE.UIManager, options: NumberDisplayOptions = {}) {
    super(ui, options);
    this.value = this.options.initialValue;
  }

  protected getAssetPaths(): { templatePath: string; stylesPath: string } {
    return {
      templatePath: NumberDisplay.metadata.assetPaths.template,
      stylesPath: NumberDisplay.metadata.assetPaths.styles,
    };
  }

  protected getDefaultOptions(): Required<NumberDisplayOptions> {
    return {
      position: 'top-center',
      visible: true,
      customClasses: [],
      customStyles: {},
      prefix: '',
      suffix: '',
      initialValue: 0,
      minValue: Number.NEGATIVE_INFINITY,
      maxValue: Number.POSITIVE_INFINITY,
      decimalPlaces: 0,
      animate: false,
      animationDuration: 300,
      label: '',
      iconHtml: '',
      trend: null,
    };
  }

  protected getInitialData(): Record<string, any> {
    return {
      value: this.formatNumber(this.value),
      prefix: this.options.prefix,
      suffix: this.options.suffix,
      label: this.options.label,
    };
  }

  protected override cacheElements(): void {
    if (!this.layout) return;
    this.valueElement = this.layout.querySelector('[data-number-display-value]') as HTMLElement | null;
    this.labelElement = this.layout.querySelector('[data-number-display-label]') as HTMLElement | null;
    this.iconElement = this.layout.querySelector('[data-number-display-icon]') as HTMLElement | null;
    this.trendElement = this.layout.querySelector('[data-number-display-trend]') as HTMLElement | null;
  }

  protected override async onInitialize(): Promise<void> {
    if (this.iconElement && this.options.iconHtml) this.iconElement.innerHTML = this.options.iconHtml;
    if (this.options.trend) this.renderTrend(this.options.trend);
    this.updateDisplay();
  }

  protected override onDataUpdate(data: Record<string, any>): void {
    if ('value' in data) this.setValue(data.value, false);
  }

  // ── Public API ──────────────────────────────────────────────────────────

  public setValue(value: number, animate: boolean = this.options.animate): void {
    const oldValue = this.value;
    this.value = this.clampValue(value);
    if (animate && oldValue !== this.value) {
      this.animateToValue(oldValue, this.value);
    } else {
      this.updateDisplay();
    }
  }

  public getValue(): number { return this.value; }

  public add(amount: number, animate: boolean = this.options.animate): void { this.setValue(this.value + amount, animate); }
  public subtract(amount: number, animate: boolean = this.options.animate): void { this.setValue(this.value - amount, animate); }
  public increment(animate: boolean = this.options.animate): void { this.add(1, animate); }
  public decrement(animate: boolean = this.options.animate): void { this.subtract(1, animate); }
  public reset(animate: boolean = this.options.animate): void { this.setValue(this.options.initialValue, animate); }

  public setPrefix(prefix: string): void {
    this.options.prefix = prefix;
    this.updateDisplay();
  }

  public setSuffix(suffix: string): void {
    this.options.suffix = suffix;
    this.updateDisplay();
  }

  public setRange(min: number, max: number): void {
    this.options.minValue = min;
    this.options.maxValue = max;
    this.value = this.clampValue(this.value);
    this.updateDisplay();
  }

  /** Update the label shown above the number. */
  public setLabel(text: string): void {
    this.options.label = text;
    if (this.labelElement) this.labelElement.textContent = text;
  }

  /** Replace the icon slot HTML (e.g. an inline SVG). */
  public setIconHtml(svg: string): void {
    this.options.iconHtml = svg;
    if (this.iconElement) this.iconElement.innerHTML = svg;
  }

  /** Set or clear the trend chip. Pass `null` to hide it. */
  public setTrend(trend: NumberDisplayTrend | null): void {
    this.options.trend = trend;
    this.renderTrend(trend);
  }

  // ── Private helpers ─────────────────────────────────────────────────────

  private clampValue(value: number): number {
    return Math.max(this.options.minValue, Math.min(this.options.maxValue, value));
  }

  private formatNumber(value: number): string {
    return value.toFixed(this.options.decimalPlaces);
  }

  private formatValueText(value: number): string {
    return `${this.options.prefix}${this.formatNumber(value)}${this.options.suffix}`;
  }

  private updateDisplay(): void {
    if (this.valueElement) this.valueElement.textContent = this.formatValueText(this.value);
  }

  private renderTrend(trend: NumberDisplayTrend | null): void {
    if (!this.trendElement) return;
    if (!trend) {
      this.trendElement.removeAttribute('data-direction');
      this.trendElement.textContent = '';
      return;
    }
    const magnitude = Math.abs(Math.round(trend.value));
    let text: string;
    if (trend.direction === 'up') text = `↑ +${magnitude}%`;
    else if (trend.direction === 'down') text = `↓ -${magnitude}%`;
    else text = '0%';
    this.trendElement.dataset.direction = trend.direction;
    this.trendElement.textContent = text;
  }

  private animateToValue(fromValue: number, toValue: number): void {
    if (this.animationId) cancelAnimationFrame(this.animationId);

    const startTime = performance.now();
    const duration = this.options.animationDuration;
    const delta = toValue - fromValue;

    const tick = (currentTime: number) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const easedProgress = 1 - Math.pow(1 - progress, 3);
      const currentValue = fromValue + delta * easedProgress;
      if (this.valueElement) this.valueElement.textContent = this.formatValueText(currentValue);
      if (progress < 1) {
        this.animationId = requestAnimationFrame(tick);
      } else {
        this.animationId = null;
        this.updateDisplay();
      }
    };

    this.animationId = requestAnimationFrame(tick);
  }

  protected override onDestroy(): void {
    if (this.animationId) {
      cancelAnimationFrame(this.animationId);
      this.animationId = null;
    }
    this.valueElement = null;
    this.labelElement = null;
    this.iconElement = null;
    this.trendElement = null;
  }
}